import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
// Icons
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // 1. IMPORT THIS
import { csrfHeaders } from "./api";
import { badgeTitle, computeBadges } from "./badges";

function SocialView({ user }) {
	// 2. INITIALIZE HOOK
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState(0);
	const [friends, setFriends] = useState([]);
	const [requests, setRequests] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [hasSearched, setHasSearched] = useState(false);

	// Loading States
	const [initialLoading, setInitialLoading] = useState(true);
	const [searchLoading, setSearchLoading] = useState(false);

	// Unfriend / cancel request
	const [friendToRemove, setFriendToRemove] = useState(null);

	useEffect(() => {
		if (!user) {
			setInitialLoading(false);
			return;
		}

		const loadData = async () => {
			setInitialLoading(true);
			try {
				const [friendsRes, requestsRes] = await Promise.all([
					fetch("/api/friends", { credentials: "include" }),
					fetch("/api/friends/requests", { credentials: "include" }),
				]);
				const friendsData = await friendsRes.json();
				const requestsData = await requestsRes.json();

				setFriends(friendsData);
				setRequests(requestsData);
			} catch (error) {
				console.error("Error loading social data", error);
			} finally {
				setInitialLoading(false);
			}
		};
		loadData();
	}, [user]);

	const handleSearch = (e) => {
		e.preventDefault();

		// 1. New Validation: Require at least 3 characters
		if (!searchQuery.trim() || searchQuery.trim().length < 3) {
			// Optional: You could set an error state here to show a message to the user
			alert("Søket må inneholde minst 3 bokstaver.");
			return;
		}

		setSearchLoading(true);
		setHasSearched(false);
		fetch(`/api/friends/search?query=${encodeURIComponent(searchQuery)}`, {
			credentials: "include",
		})
			.then((res) => {
				if (!res.ok) throw new Error("Search failed");
				return res.json();
			})
			.then((data) => {
				setSearchResults(data);
				setHasSearched(true);
				setSearchLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setSearchLoading(false);
			});
	};

	const sendRequest = async (userId) => {
		const res = await fetch(`/api/friends/request/${userId}`, {
			method: "POST",
			credentials: "include",
			headers: csrfHeaders(),
		});
		if (res.ok)
			setSearchResults((prev) =>
				prev.map((u) => (u.id === userId ? { ...u, status: "SENT" } : u)),
			);
	};

	const cancelRequest = async (target) => {
		const res = await fetch(`/api/friends/${target.friendshipId}`, {
			method: "DELETE",
			credentials: "include",
			headers: csrfHeaders(),
		});
		if (res.ok)
			setSearchResults((prev) =>
				prev.map((u) =>
					u.id === target.id ? { ...u, status: "NONE", friendshipId: null } : u,
				),
			);
		else alert("Kunne ikke avbryte forespørselen.");
	};

	const respondToRequest = async (friendshipId, action) => {
		const res = await fetch(
			`/api/friends/respond/${friendshipId}?action=${action}`,
			{
				method: "POST",
				credentials: "include",
				headers: csrfHeaders(),
			},
		);
		if (res.ok) {
			setRequests((prev) =>
				prev.filter((r) => r.friendshipId !== friendshipId),
			);
			if (action === "ACCEPT") {
				fetch("/api/friends", { credentials: "include" })
					.then((res) => res.json())
					.then(setFriends);
			}
		}
	};

	const removeFriend = async () => {
		if (!friendToRemove) return;
		const res = await fetch(`/api/friends/${friendToRemove.friendshipId}`, {
			method: "DELETE",
			credentials: "include",
			headers: csrfHeaders(),
		});
		if (res.ok) {
			fetch("/api/friends", { credentials: "include" })
				.then((res) => res.json())
				.then(setFriends);
		} else {
			alert("Kunne ikke fjerne vennen.");
		}
		setFriendToRemove(null);
	};

	const getRankDisplay = (index) => {
		if (index === 0)
			return (
				<EmojiEventsIcon
					sx={{
						color: "#FFD700",
						fontSize: 32,
						filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
					}}
				/>
			);
		if (index === 1)
			return <EmojiEventsIcon sx={{ color: "#C0C0C0", fontSize: 28 }} />;
		if (index === 2)
			return <EmojiEventsIcon sx={{ color: "#CD7F32", fontSize: 26 }} />;
		return (
			<Typography
				variant="h6"
				sx={{
					color: "#888",
					fontWeight: "bold",
					width: 30,
					textAlign: "center",
				}}
			>
				#{index + 1}
			</Typography>
		);
	};

	const renderSkeletonList = () => (
		<Box sx={{ mt: 2 }}>
			{[1, 2, 3, 4].map((i) => (
				<Paper
					key={i}
					elevation={0}
					sx={{
						p: 2,
						mb: 2,
						display: "flex",
						alignItems: "center",
						borderRadius: 3,
						border: "1px solid #f0f0f0",
					}}
				>
					<Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
					<Box sx={{ flex: 1 }}>
						<Skeleton variant="text" width="60%" height={24} />
						<Skeleton variant="text" width="40%" height={16} />
					</Box>
					<Skeleton
						variant="rectangular"
						width={50}
						height={30}
						sx={{ borderRadius: 1 }}
					/>
				</Paper>
			))}
		</Box>
	);

	// --- GUEST VIEW ---
	if (!user) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "70vh",
					p: 3,
				}}
			>
				<Paper
					elevation={0}
					sx={{
						p: 4,
						textAlign: "center",
						maxWidth: 400,
						bgcolor: "#f1f8e9",
						borderRadius: 4,
						border: "1px dashed #2E7D32",
					}}
				>
					<GroupIcon sx={{ fontSize: 60, color: "#2E7D32", mb: 2 }} />
					<Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
						Logg inn for å finne venner, sammenligne score og klatre på
						topplisten.
					</Typography>

					{/* 3. UPDATED BUTTON */}
					<Button
						variant="contained"
						onClick={() => navigate("/login")}
						sx={{
							bgcolor: "#2E7D32",
							borderRadius: 2,
							px: 4,
							textTransform: "none",
							fontWeight: "bold",
							"&:hover": { bgcolor: "#1b5e20" },
						}}
					>
						Logg inn
					</Button>
				</Paper>
			</Box>
		);
	}

	// --- NORMAL VIEW (Logged In) ---
	return (
		// 4. UPDATED CONTAINER (Box instead of div)
		<Box
			sx={{
				p: { xs: 1.5, sm: 3 },
				pb: 10,
				maxWidth: "md",
				margin: "0 auto",
			}}
		>
			<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
				<Tabs
					value={activeTab}
					onChange={(_e, val) => setActiveTab(val)}
					variant="fullWidth"
					textColor="primary"
					indicatorColor="primary"
					sx={{
						"& .MuiTab-root": {
							textTransform: "none",
							fontWeight: 600,
							fontSize: "1rem",
						},
						"& .Mui-selected": { color: "#2E7D32" },
						"& .MuiTabs-indicator": { backgroundColor: "#2E7D32" },
					}}
				>
					<Tab label={`Toppliste (${friends.length})`} />
					<Tab
						label="Finn golfere"
						icon={<SearchIcon fontSize="small" />}
						iconPosition="start"
					/>
				</Tabs>
			</Box>

			{/* TAB 1: FRIENDS */}
			{activeTab === 0 && (
				<Box>
					{initialLoading && renderSkeletonList()}
					{!initialLoading && (
						<>
							{requests.length > 0 && (
								<Paper
									elevation={0}
									sx={{
										mb: 3,
										p: 2,
										background: "#fff3e0",
										border: "1px solid #ffe0b2",
										borderRadius: 3,
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											mb: 1,
											color: "#e65100",
											gap: 1,
										}}
									>
										<NotificationsActiveIcon fontSize="small" />
										<Typography variant="subtitle2" fontWeight="bold">
											Nye venneforespørsler
										</Typography>
									</Box>
									{requests.map((req) => (
										<Box
											key={req.id}
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mt: 1,
												background: "white",
												p: 1.5,
												borderRadius: 2,
											}}
										>
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
											>
												<Avatar
													src={req.avatar}
													sx={{ width: 32, height: 32 }}
												/>
												<Typography variant="body2" fontWeight="600">
													{req.displayName}
												</Typography>
											</Box>
											<Box sx={{ display: "flex", gap: 1 }}>
												<IconButton
													size="small"
													onClick={() =>
														respondToRequest(req.friendshipId, "ACCEPT")
													}
													sx={{ color: "green", bgcolor: "#e8f5e9" }}
												>
													<CheckCircleIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													onClick={() =>
														respondToRequest(req.friendshipId, "REJECT")
													}
													sx={{ color: "red", bgcolor: "#ffebee" }}
												>
													<CancelIcon fontSize="small" />
												</IconButton>
											</Box>
										</Box>
									))}
								</Paper>
							)}
							{friends.length === 0 ? (
								<Box sx={{ textAlign: "center", mt: 5, color: "#888" }}>
									<PersonAddIcon
										sx={{ fontSize: 60, color: "#e0e0e0", mb: 2 }}
									/>
									<Typography>Ingen venner ennå.</Typography>
									<Button
										onClick={() => setActiveTab(1)}
										sx={{ mt: 1, color: "#2E7D32" }}
									>
										Finn golfere
									</Button>
								</Box>
							) : (
								<List sx={{ width: "100%", padding: 0 }}>
									{friends.map((friend, index) => {
										const isMe = friend.status === "ME";
										const title = badgeTitle(
											computeBadges({
												clubs: friend.totalCourses,
												rounds: friend.totalRounds,
												fylkerComplete: friend.fylkerComplete,
												fylkerTotal: friend.fylkerTotal,
											}),
										);
										const TierIcon = title?.tier.Icon;
										return (
											<Paper
												key={friend.id}
												elevation={0}
												sx={{
													mb: 1.5,
													borderRadius: 3,
													border: isMe
														? "2px solid #4CAF50"
														: "1px solid #f0f0f0",
													background: isMe ? "#f1f8e9" : "white",
													overflow: "hidden",
													transition: "transform 0.2s",
													"&:hover": {
														transform: "translateY(-2px)",
														boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
													},
												}}
											>
												<ListItem sx={{ py: 2 }}>
													<Box
														sx={{
															mr: 2,
															display: "flex",
															justifyContent: "center",
															width: 40,
														}}
													>
														{getRankDisplay(index)}
													</Box>
													<ListItemAvatar>
														<Box
															sx={{
																position: "relative",
																width: 40,
																height: 40,
															}}
														>
															<Avatar
																src={
																	friend.avatar ||
																	`https://ui-avatars.com/api/?name=${friend.displayName}`
																}
																sx={{
																	width: 40,
																	height: 40,
																	// The green "this is you" marker is the row's
																	// Paper border, not this one, so a tier ring
																	// here collides with nothing.
																	border: title
																		? `2px solid ${title.tier.color}`
																		: "2px solid white",
																	boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
																}}
															/>
															{title && (
																<Box
																	sx={{
																		position: "absolute",
																		bottom: -3,
																		right: -4,
																		width: 19,
																		height: 19,
																		borderRadius: "50%",
																		bgcolor: "#fff",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																		boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
																	}}
																>
																	<TierIcon
																		sx={{
																			fontSize: 13,
																			color: title.tier.color,
																		}}
																	/>
																</Box>
															)}
														</Box>
													</ListItemAvatar>
													<Box sx={{ flex: 1 }}>
														<Typography
															variant="subtitle1"
															fontWeight="700"
															color="text.primary"
														>
															{friend.displayName}
														</Typography>
														{title && (
															<Typography
																variant="caption"
																sx={{
																	display: "block",
																	fontWeight: 700,
																	lineHeight: 1.3,
																	...(title.tier.gradient
																		? {
																				background: title.tier.gradient,
																				backgroundClip: "text",
																				WebkitBackgroundClip: "text",
																				color: "transparent",
																			}
																		: { color: title.tier.color }),
																}}
															>
																{title.label}
															</Typography>
														)}
														{isMe && (
															<Chip
																label="Deg"
																size="small"
																color="success"
																sx={{ height: 20, fontSize: "0.65rem" }}
															/>
														)}
													</Box>
													<Box
														sx={{ textAlign: "right", display: "flex", gap: 3 }}
													>
														<Box>
															<Typography
																variant="h6"
																sx={{
																	lineHeight: 1,
																	fontWeight: 700,
																	color: "#333",
																}}
															>
																{friend.totalCourses}
															</Typography>
															<Typography
																variant="caption"
																sx={{ color: "#999", fontSize: "0.7rem" }}
															>
																BANER
															</Typography>
														</Box>
														<Box>
															<Typography
																variant="h6"
																sx={{
																	lineHeight: 1,
																	fontWeight: 700,
																	color: "#333",
																}}
															>
																{friend.totalRounds}
															</Typography>
															<Typography
																variant="caption"
																sx={{ color: "#999", fontSize: "0.7rem" }}
															>
																RUNDER
															</Typography>
														</Box>
													</Box>
													{!isMe && friend.friendshipId && (
														<IconButton
															size="small"
															aria-label="Fjern venn"
															onClick={() => setFriendToRemove(friend)}
															sx={{
																ml: 1,
																color: "#bdbdbd",
																"&:hover": {
																	color: "#d32f2f",
																	bgcolor: "#ffebee",
																},
															}}
														>
															<PersonRemoveIcon fontSize="small" />
														</IconButton>
													)}
												</ListItem>
											</Paper>
										);
									})}
								</List>
							)}
						</>
					)}
				</Box>
			)}

			{/* TAB 2: SEARCH */}
			{activeTab === 1 && (
				<Box>
					<form onSubmit={handleSearch}>
						<TextField
							fullWidth
							placeholder="Søk etter e-post eller brukernavn..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							variant="outlined"
							sx={{
								mb: 3,
								"& .MuiOutlinedInput-root": {
									borderRadius: 3,
									background: "white",
								},
								"& .Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: "#2E7D32",
								},
							}}
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon color="action" />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<Button
												type="submit"
												variant="contained"
												disableElevation
												sx={{
													borderRadius: 2,
													bgcolor: "#2E7D32",
													textTransform: "none",
												}}
											>
												Søk
											</Button>
										</InputAdornment>
									),
								},
							}}
						/>
					</form>
					{searchLoading && renderSkeletonList()}
					<List>
						{searchResults.map((user) => (
							<Paper
								key={user.id}
								elevation={0}
								sx={{ mb: 1.5, borderRadius: 3, border: "1px solid #eee" }}
							>
								<ListItem>
									<ListItemAvatar>
										<Avatar src={user.avatar} />
									</ListItemAvatar>
									<Box sx={{ flex: 1 }}>
										{/* This will now show "Casper" instead of "Casper Benjamin Karlsen" */}
										<Typography variant="subtitle1" fontWeight="600">
											{user.displayName}
										</Typography>

										{/* Shows email to confirm identity */}
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontSize: "0.85rem" }}
										>
											{user.email}
										</Typography>
									</Box>
									<Box>
										{user.status === "NONE" && (
											<Button
												variant="outlined"
												size="small"
												startIcon={<PersonAddIcon />}
												onClick={() => sendRequest(user.id)}
												sx={{
													color: "#2E7D32",
													borderColor: "#2E7D32",
													borderRadius: 2,
												}}
											>
												Legg til
											</Button>
										)}
										{user.status === "SENT" && (
											<Chip
												label="Sendt"
												size="small"
												onDelete={
													user.friendshipId
														? () => cancelRequest(user)
														: undefined
												}
												deleteIcon={
													<CancelIcon titleAccess="Avbryt forespørsel" />
												}
												sx={{
													bgcolor: "#fff3e0",
													color: "#e65100",
													fontWeight: 600,
													"& .MuiChip-deleteIcon": {
														color: "#e65100",
														"&:hover": { color: "#b71c1c" },
													},
												}}
											/>
										)}
										{user.status === "RECEIVED" && (
											<Chip
												label="Venter"
												size="small"
												color="error"
												variant="outlined"
											/>
										)}
										{user.status === "FRIENDS" && (
											<Chip
												icon={<CheckCircleIcon />}
												label="Venner"
												size="small"
												color="success"
												variant="outlined"
											/>
										)}
										{user.status === "ME" && (
											<Typography
												variant="caption"
												sx={{ fontStyle: "italic", color: "#999" }}
											>
												Deg
											</Typography>
										)}
									</Box>
								</ListItem>
							</Paper>
						))}
					</List>
					{hasSearched && searchResults.length === 0 && !searchLoading && (
						<Box sx={{ textAlign: "center", mt: 5, color: "#999" }}>
							<SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
							<Typography>Ingen golfere funnet.</Typography>
						</Box>
					)}
				</Box>
			)}

			{/* --- REMOVE FRIEND CONFIRMATION DIALOG --- */}
			<Dialog
				open={Boolean(friendToRemove)}
				onClose={() => setFriendToRemove(null)}
				slotProps={{ paper: { sx: { borderRadius: 3 } } }}
			>
				<DialogTitle>Fjerne venn?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Er du sikker på at du vil fjerne {friendToRemove?.displayName} som
						venn? Dette kan ikke angres.
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={() => setFriendToRemove(null)} color="inherit">
						Avbryt
					</Button>
					<Button
						onClick={removeFriend}
						color="error"
						variant="contained"
						disableElevation
					>
						Fjern
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default SocialView;
